import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Sparkles, RefreshCw, ChevronRight, AlertTriangle } from 'lucide-react';

/**
 * MorningBriefingCard — Claude-narrated morning brief on the CEO/leader
 * dashboard. Cached per user per IST day; re-generated at 07:00 IST by cron.
 * Auto-hides after 16:00 IST so it doesn't stale the dashboard in the evening.
 */
const SEV_BG = { critical: '#FEE2E2', high: '#FEF3E5', warn: '#FEF9C3', info: '#F7F5F2' };
const SEV_FG = { critical: '#991B1B', high: '#9A3412', warn: '#854D0E', info: '#6B7280' };

const fmtINR = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
};

export default function MorningBriefingCard({ className = '' }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/mira-intel/briefing');
      setBrief(r.data);
    } catch {
      // silent — dashboard still works without a briefing
    } finally {
      setLoading(false);
    }
  };

  const regen = async () => {
    setRegenerating(true);
    try {
      const r = await api.post('/mira-intel/briefing/regenerate');
      setBrief(r.data);
    } finally {
      setRegenerating(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Hide after 16:00 IST so it doesn't stale the afternoon dashboard.
  // Override with ?briefing=always in the URL for testing or CEO-on-the-go.
  const hourIST = (new Date().getUTCHours() + 5 + (new Date().getUTCMinutes() + 30) / 60) % 24;
  const forceShow = typeof window !== 'undefined' && window.location.search.includes('briefing=always');
  if (dismissed || (!forceShow && hourIST >= 16) || loading || !brief?.narrative) return null;

  return (
    <div className={`rounded-lg border overflow-hidden ${className}`}
         style={{ borderColor: '#E5E1DC', backgroundColor: '#FDFCFA' }}
         data-testid="morning-briefing-card">
      <div className="flex items-center gap-2 px-4 py-2 border-b"
           style={{ borderColor: '#F3F0EB', backgroundColor: '#F7F5F2' }}>
        <Sparkles size={14} style={{ color: '#2A7B88' }} />
        <span className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: '#6B7280' }}>
          Mira's morning briefing
        </span>
        <span className="text-[10px]" style={{ color: '#9CA3AF' }}>{brief.date}</span>
        <button onClick={regen} disabled={regenerating}
                className="ml-auto text-[10px] flex items-center gap-1 hover:text-[#2A7B88]"
                style={{ color: '#9CA3AF' }} title="Regenerate"
                data-testid="briefing-regen-btn">
          <RefreshCw size={10} className={regenerating ? 'animate-spin' : ''} />
          {regenerating ? 'Thinking…' : 'Refresh'}
        </button>
        <button onClick={() => setDismissed(true)} className="text-[10px] ml-2 hover:text-[#1B2A4A]"
                style={{ color: '#9CA3AF' }} title="Hide for today">×</button>
      </div>
      <div className="px-4 py-3">
        <p className="text-sm leading-relaxed" style={{ color: '#1B2A4A' }}
           data-testid="briefing-narrative">{brief.narrative}</p>
        {brief.anomalies?.length > 0 && (
          <div className="mt-3 pt-3 border-t flex flex-wrap gap-1.5" style={{ borderColor: '#F3F0EB' }}>
            <span className="text-[10px] uppercase tracking-wide self-center mr-1" style={{ color: '#9CA3AF' }}>
              <AlertTriangle size={10} className="inline mr-0.5" />
              Active signals:
            </span>
            {brief.anomalies.slice(0, 6).map((a, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: SEV_BG[a.severity] || '#F7F5F2',
                             color: SEV_FG[a.severity] || '#6B7280' }}
                    data-testid={`briefing-anomaly-${i}`}>
                {a.title}
              </span>
            ))}
          </div>
        )}
        {(brief.renewals_30d?.length > 0 || brief.hot_deals?.length > 0) && (
          <div className="mt-2 flex flex-wrap gap-3 text-[10px]" style={{ color: '#6B7280' }}>
            {brief.renewals_30d?.length > 0 && (
              <span>🔁 {brief.renewals_30d.length} renewal{brief.renewals_30d.length > 1 ? 's' : ''} in 30d
                {' · '}{brief.renewals_30d.slice(0, 2).map(r => r.client_name).join(', ')}
              </span>
            )}
            {brief.hot_deals?.length > 0 && (
              <span>🔥 {brief.hot_deals.length} hot deal{brief.hot_deals.length > 1 ? 's' : ''} ·
                {' '}{fmtINR(brief.hot_deals.reduce((s, d) => s + (Number(d.deal_value) || 0), 0))} TCV
              </span>
            )}
          </div>
        )}
        <button onClick={() => window.location.assign('/mira')}
                className="mt-3 text-[11px] font-medium inline-flex items-center gap-1 hover:opacity-75"
                style={{ color: '#2A7B88' }}
                data-testid="briefing-ask-mira-btn">
          Ask Mira about this <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}
